/**
 * Social Platform Configuration
 *
 * Constants and utilities for social media embed handling.
 * Includes platform detection, branding (icons/colors), and metadata extraction.
 */

/**
 * Supported social media platforms for embeds
 */
export type SocialPlatform =
  | "instagram"
  | "x"
  | "tiktok"
  | "youtube"
  | "facebook"
  | "threads"
  | "spotify"
  | "linkedin"
  | "unknown";

/**
 * SVG icons for social media platforms (inline strings for innerHTML rendering)
 */
export const PLATFORM_ICONS: Record<SocialPlatform, string> = {
  instagram: `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>`,
  x: `<svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>`,
  tiktok: `<svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/></svg>`,
  youtube: `<svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>`,
  facebook: `<svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>`,
  threads: `<svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor"><path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.96-.065-1.182.408-2.256 1.333-3.022.857-.709 2.041-1.129 3.527-1.25.895-.073 1.79-.042 2.665.09-.07-.646-.244-1.178-.53-1.577-.387-.543-1.063-.845-1.964-.878-1.183-.043-2.31.419-2.903 1.192l-1.476-1.072c.924-1.198 2.543-1.953 4.478-1.869 1.528.056 2.716.593 3.432 1.553.578.775.924 1.782 1.028 2.996 1.026.31 1.9.762 2.604 1.35.996.832 1.59 1.924 1.763 3.244.239 1.822-.262 3.63-1.412 5.093-1.348 1.716-3.473 2.745-6.323 3.061-.476.052-.955.078-1.434.078z"/></svg>`,
  spotify: `<svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>`,
  linkedin: `<svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>`,
  unknown: `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>`,
};

/**
 * Brand colors for each platform
 */
export const PLATFORM_COLORS: Record<SocialPlatform, string> = {
  instagram: "#E4405F",
  x: "#000000",
  tiktok: "#000000",
  youtube: "#FF0000",
  facebook: "#1877F2",
  threads: "#000000",
  spotify: "#1DB954",
  linkedin: "#0A66C2",
  unknown: "#30373d",
};

/**
 * Platform display names and privacy policy URLs for GDPR consent overlay
 */
export const PLATFORM_INFO: Record<SocialPlatform, { name: string; privacyUrl: string }> = {
  instagram: {
    name: "Instagram",
    privacyUrl: "https://privacycenter.instagram.com/policy",
  },
  x: {
    name: "X (Twitter)",
    privacyUrl: "https://twitter.com/en/privacy",
  },
  tiktok: {
    name: "TikTok",
    privacyUrl: "https://www.tiktok.com/legal/privacy-policy",
  },
  youtube: {
    name: "YouTube",
    privacyUrl: "https://policies.google.com/privacy",
  },
  facebook: {
    name: "Facebook",
    privacyUrl: "https://www.facebook.com/privacy/policy",
  },
  threads: {
    name: "Threads",
    privacyUrl: "https://privacycenter.instagram.com/policy",
  },
  spotify: {
    name: "Spotify",
    privacyUrl: "https://www.spotify.com/legal/privacy-policy/",
  },
  linkedin: {
    name: "LinkedIn",
    privacyUrl: "https://www.linkedin.com/legal/privacy-policy",
  },
  unknown: {
    name: "Externer Inhalt",
    privacyUrl: "#",
  },
};

/**
 * Regex patterns for extracting media IDs from social media URLs
 */
const MEDIA_ID_PATTERNS: Record<SocialPlatform, RegExp[]> = {
  instagram: [/instagram\.com\/(?:p|reel|tv)\/([a-zA-Z0-9_-]+)/i],
  x: [/(?:twitter\.com|x\.com)\/\w+\/status\/(\d+)/i],
  tiktok: [/tiktok\.com\/@[\w.-]+\/video\/(\d+)/i, /vm\.tiktok\.com\/([a-zA-Z0-9]+)/i],
  youtube: [
    /youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/i,
    /youtu\.be\/([a-zA-Z0-9_-]+)/i,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]+)/i,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/i,
  ],
  facebook: [
    /facebook\.com\/[\w.-]+\/(?:posts|videos)\/(\d+)/i,
    /facebook\.com\/watch\/?\?v=(\d+)/i,
    /facebook\.com\/events\/(\d+)/i,
    /facebook\.com\/photo[s]?[./][\w.-]*(\d+)/i,
    /facebook\.com\/reel\/(\d+)/i,
  ],
  threads: [/threads\.net\/@[\w.-]+\/post\/([a-zA-Z0-9_-]+)/i],
  spotify: [/spotify\.com\/(track|album|playlist|episode|show)\/([a-zA-Z0-9]+)/i],
  linkedin: [
    /linkedin\.com\/posts\/[\w-]+[-_](\d+)[-_]/i,
    /linkedin\.com\/feed\/update\/urn:li:activity:(\d+)/i,
    /linkedin\.com\/embed\/feed\/update\/urn:li:share:(\d+)/i,
    /linkedin\.com\/embed\/feed\/update\/urn:li:ugcPost:(\d+)/i,
  ],
  unknown: [],
};

/**
 * Extracts video/post ID from various social media URLs
 *
 * @param url - Social media URL
 * @param platform - Platform type to match against
 * @returns Media ID if found, null otherwise
 */
export function extractMediaId(url: string, platform: SocialPlatform): string | null {
  const patterns = MEDIA_ID_PATTERNS[platform] || [];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) {
      // Spotify returns two groups (type and id), use the second
      return match[2] || match[1];
    }
  }

  return null;
}
